import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import Kategori services - Ensure this path is correct: src/services/kategoriService.js
import {
  fetchKategori,
  createKategori,
  updateKategori,
  deleteKategori
} from '../../services/Master/kategoriService';

// Import common components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const Kategori = () => {
  // Meta title for the browser tab
  document.title = "Kategori Paket "; // Updated for clarity and SEO

  // State Management
  const [modal, setModal] = useState(false); 
  const [isEdit, setIsEdit] = useState(false); // True if editing, false if adding
  const [currentKategori, setCurrentKategori] = useState(null); // Stores the category data when editing or deleting
  const [data, setData] = useState([]); // Stores the list of categories fetched from the API
  const [loading, setLoading] = useState(true); // Manages loading spinner display
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  }); // Manages pagination metadata
  const [filters, setFilters] = useState({
    jenisPaket: "",
    jenisSeleksi: "",
    kategoriPaket: "" // Filter by 'kategori' field itself
  });
  const [deleteModal, setDeleteModal] = useState(false); // Controls the delete confirmation modal visibility

  // --- API Interaction Functions ---

  /**
   * Fetches category data from the API based on current pagination and filter settings.
   * @param {number} page - The page number to fetch.
   * @param {number} per_page - The number of items per page.
   * @param {object} filterParams - Current filter values.
   */
  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchKategori(page, per_page, filterParams);

      // Update data and pagination state based on API response
      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error(error.response?.data?.message || "Gagal memuat data kategori.");
    } finally {
      setLoading(false);
    }
  };

  // Effect hook to fetch data on initial component mount
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array means this runs once on mount

  // --- Filter Handlers ---

  /**
   * Handles changes in filter input fields.
   * @param {object} e - The event object from the input change.
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));

    // Re-fetch data starting from page 1 with new filters
    fetchData(1, pagination.per_page, {
      ...filters,
      [name]: value
    });
  };

  /**
   * Clears all active filters and re-fetches data.
   */
  const clearFilters = () => {
    setFilters({
      jenisPaket: "",
      jenisSeleksi: "",
      kategoriPaket: ""
    });
    fetchData(); // Fetch data without any filters
  };

  // --- Modal and Form Handlers ---

  /**
   * Toggles the visibility of the add/edit modal.
   * Resets form and selected category data if modal is closing.
   */
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setCurrentKategori(null);
      setIsEdit(false);
      validation.resetForm(); // Reset form validation state
    } else {
      setModal(true);
    }
  };

  /**
   * Handles opening the modal for editing an existing category.
   * @param {object} kategoriData - The data of the category to be edited.
   */
  const handleKategoriClick = (kategoriData) => {
    setCurrentKategori(kategoriData);
    setIsEdit(true);
    toggleModal();
  };

  // Form validation using Formik and Yup
  const validation = useFormik({
    enableReinitialize: true, // Reinitialize form when initialValues change
    initialValues: {
      kategori: currentKategori?.kategori || "",
    },
    validationSchema: Yup.object({
      kategori: Yup.string().required("Nama Kategori wajib diisi"),
    }),
    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          // Update existing category
          response = await updateKategori(currentKategori.id_kategori_paket, values);
        } else {
          // Create new category
          response = await createKategori(values);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Kategori berhasil diperbarui." : "Kategori berhasil ditambahkan."));
          fetchData(pagination.page, pagination.per_page, filters); // Re-fetch data to update table
          toggleModal(); // Close modal on success
        } else {
          // Handle specific backend errors
          if (response.code === 409) {
            toast.error(response.message || "Terjadi Kesalahan saat menyimpan.");
          } else {
            toast.error(response.message || "Gagal menyimpan kategori.");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan.");
      }
    },
  });

  // --- Delete Handlers ---

  /**
   * Prepares for deleting a category by setting the current category and opening the delete modal.
   * @param {object} kategoriData - The data of the category to be deleted.
   */
  const onClickDelete = (kategoriData) => {
    setCurrentKategori(kategoriData);
    setDeleteModal(true);
  };

  /**
   * Closes the delete confirmation modal and resets the selected category.
   */
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setCurrentKategori(null);
  };

  /**
   * Confirms and executes the category deletion.
   */
  const handleConfirmDelete = async () => {
    try {
      if (currentKategori && currentKategori.id_kategori_paket) {
        const response = await deleteKategori(currentKategori.id_kategori_paket);
        if (response.success) {
          toast.success("Kategori berhasil dihapus.");
          fetchData(pagination.page, pagination.per_page, filters); // Re-fetch data after deletion
        } else {
          toast.error(response.message || "Gagal menghapus kategori.");
        }
      }
    } catch (error) {
      console.error("Error deleting kategori:", error);
      toast.error(error.response?.data?.message || "Gagal menghapus kategori.");
    } finally {
      handleCloseDeleteModal(); // Always close modal
    }
  };

  // --- Table Configuration ---

  // Memoized columns for the TableContainer to prevent unnecessary re-renders
  const columns = useMemo(
    () => [
      {
        header: "Nama Kategori",
        accessorKey: "kategori",
        enableColumnFilter: false,
      },
      {
        header: 'Created At',
        accessorKey: "created_at", // Matches database column name 'created'
        enableColumnFilter: false,
        cell: (cellProps) => {
          const rawDateValue = cellProps.getValue();
          // Added check for invalid date values
          if (!rawDateValue) {
            return ''; // Return empty string or a placeholder if date is null/undefined/empty
          }
          const date = new Date(rawDateValue);
          // Check if the Date object is valid (e.g., 'new Date("invalid string")' results in Invalid Date)
          if (isNaN(date.getTime())) {
            console.warn("Invalid date value received for 'created':", rawDateValue);
            return 'Invalid Date'; // Or return '' if you prefer to hide it
          }
          return date.toISOString().split('T')[0]; // Format to YYYY-MM-DD
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Link
              to="#"
              className="btn btn-sm btn-soft-info"
              onClick={() => {
                const kategoriData = cellProps.row.original;
                handleKategoriClick(kategoriData);
              }}
              id={`edittooltip-${cellProps.row.original.id_kategori_paket}`}
            >
              <i className="mdi mdi-pencil-outline" />
              <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_kategori_paket}`} >
                Edit
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="btn btn-sm btn-soft-danger"
              onClick={() => {
                const kategoriData = cellProps.row.original;
                onClickDelete(kategoriData);
              }}
              id={`deletetooltip-${cellProps.row.original.id_kategori_paket}`}
            >
              <i className="mdi mdi-delete-outline" />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_kategori_paket}`}>
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        )
      },
    ],
    [] // Empty dependency array ensures columns are created only once
  );

  // --- Custom Pagination Component ---
  // This component renders the pagination controls based on the `pagination` state.
  const CustomPagination = () => {
    const { page, last_page } = pagination;

    // Do not render pagination if there's only one page or no data
    if (last_page <= 1 && pagination.total === 0) return null;

    // Generate visible page numbers (e.g., 1 2 3 4 5 ...)
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5; // Max number of page buttons to show at once
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      // Adjust startPage if we're near the end, to keep maxVisiblePages consistent
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, pagination.per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        {/* Displaying current data range and total */}
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * pagination.per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * pagination.per_page, pagination.total)}</span> dari{' '}
          <span className="fw-semibold">{pagination.total}</span> data
        </div>

        {/* Pagination controls */}
        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            {/* Previous Page Button */}
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, pagination.per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {/* Render dynamically generated page numbers */}
            {getPageNumbers()}

            {/* Next Page Button */}
            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, pagination.per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  // --- Main Render Method ---
  return (
    <React.Fragment>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />

      <div className="page-content">
        <div className="container-fluid">
          {/* Breadcrumbs for navigation */}
          <Breadcrumbs title="Kategori Paket" breadcrumbItem="List Kategori Paket" />

          {/* Conditional rendering for loading spinner */}
          {loading ? <Spinners setLoading={setLoading} /> : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                      <h5 className="mb-0 card-title flex-grow-1">List Kategori Paket</h5>
                      <div className="d-flex align-items-center">
                        <Button color="primary" onClick={toggleModal}>
                          <i className="mdi mdi-plus me-1"></i> Tambah
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                  <CardBody>


                    {/* Table Container for displaying data */}
                    <TableContainer
                      columns={columns}
                      data={data}
                      isGlobalFilter={false} // Disable internal global filter if you're using custom filters
                      isPagination={false} // Disable internal pagination as you have a CustomPagination component
                      customPageSize={pagination.per_page}
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    />

                    {/* Custom Pagination Component */}
                    <CustomPagination />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Add/Edit Category Modal */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit Kategori Paket" : "Tambah Kategori Paket"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="kategori">Nama Kategori</Label>
                      <Input
                        id="kategori"
                        name="kategori"
                        type="text"
                        placeholder="Masukkan Nama Kategori"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.kategori || ""}
                        invalid={!!(validation.touched.kategori && validation.errors.kategori)}
                      />
                      <FormFeedback>{validation.errors.kategori}</FormFeedback>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="text-end">
                      <Button color="success" type="submit">
                        {isEdit ? "Update" : "Simpan"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      {/* Toast notifications container */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </React.Fragment>
  );
}

export default Kategori;